import {Injectable} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Claim2CustomerI, Claim2UserI, ClaimI} from './pages/admin/interfaces/claim.interface';
import {ApiService} from './core/services/api.service';
import {DialogService} from './core/services/dialog.service';
import {StorageService} from './core/services/storage.service';
import {CustomerData, ExtendedScopeI} from './pages/admin/interfaces/customer-data';
import {LogT} from './pages/admin/interfaces/log';
import {Scope} from './pages/admin/interfaces/scope.interface';
import {Registration, RelatedCustomer, UserData} from './pages/admin/interfaces/user-data';
import {ActivationT} from "./pages/admin/interfaces/activation";
import {UserI} from './pages/admin/interfaces/user.interface';
import {CustomerI} from './pages/admin/interfaces/customer.interface';
import {CustomerDomainI} from './pages/admin/interfaces/customer-domain.interface';

@Injectable({
  providedIn: 'root'
})
export class AppService {

  public scopes: Scope[];

  public claims: ClaimI[];

  public searchResults: { found: UserI[], total: any };

  public customerSearchResults: { found: CustomerI[], total: any };

  constructor(public api: ApiService, public storage: StorageService, public toast: MatSnackBar, public dialog: DialogService) {
  }

  // helpers

  public dateFormatToDB(date: Date | string | any) {
    if (typeof date === 'object') {
      // date.setHours(0, 0, 0, 0);
      return date.toISOString();
    } else if (typeof date === 'string') {
      return date;
    }
  }

  public async getScopes(): Promise<Scope[]> {
    if (!this.scopes) {
      this.scopes = await this.api.get('scopes');
    }

    return this.scopes;
  }

  public async getClaims(): Promise<ClaimI[]> {
    if (!this.claims) {
      this.claims = await this.api.get('claims');
    }

    return this.claims;
  }

  // edit-user-routes

  public async usersFind(params: object): Promise<{ found: UserI[], total: any }> {
    return await this.api.post('admin/users/', params).then((res: { found: UserI[], total: any }) => {
      this.searchResults = res;
      return res;
    });
  }

  public async userData(id: string): Promise<UserData> {
    return await this.api.get('admin/user/' + id) as UserData;
  }

  public async userUpdate(user: UserI, uuId: string): Promise<any> {
    return await this.api.put('admin/user/', {user, uuId});
  }

  public async lostPassword(email: string): Promise<boolean> {
    return await this.api.post('lost-password', {email});
  }

  public async relatedCustomersLive(name: string): Promise<RelatedCustomer[]> {
    return await this.api.get('admin/related-customers/' + name);
  }

  public async relatedCustomers(relatedCustomers: string[]): Promise<RelatedCustomer[]> {
    return await this.api.post('admin/related-customers/', relatedCustomers);
  }

  public async newInvitation(params: { c2cId: string, userId?: string, email?: string }): Promise<boolean> {
    return await this.api.post('admin/claim2user/invitation/', params);
  }

  public async claim2userCreate(params: { userId: string, external_claim_id: string }): Promise<boolean> {
    return await this.api.post('admin/claim2user/', params);
  }

  public async claim2userDelete(id: string): Promise<boolean> {
    return await this.api.delete('admin/claim2user/' + id);
  }

  // edit-c2c-routes

  public async customersFind(params: object): Promise<{ found: CustomerI[], total: any }> {
    return await this.api.post('admin/customers/', params).then((res: { found: CustomerI[], total: any }) => {
      this.customerSearchResults = res;
      return res;
    });
  }

  public async customerData(id: string): Promise<CustomerData> {
    return await this.api.get('admin/customer/' + id) as CustomerData;
  }

  public async customerClaims(params: object): Promise<ExtendedScopeI> {
    return await this.api.post('admin/customer/c2c/', params);
  }

  public async customerClaimSubscribers(params: object): Promise<{ found: Claim2UserI[], total: any }> {
    return await this.api.post('admin/customer/c2c/subscribers', params);
  }

  public async claim2customerCreate(c2c: Claim2CustomerI, uuId: string): Promise<any> {
    return await this.api.post('admin/claim2customer/', {c2c, uuId});
  }

  public async claim2customerUpdate(c2c: Claim2CustomerI, uuId: string): Promise<any> {
    return await this.api.put('admin/claim2customer/', {c2c, uuId});
  }

  public async claim2customerDelete(id: string): Promise<Claim2CustomerI> {
    return await this.api.delete('admin/claim2customer/' + id);
  }

  public async customerDomainCreate(domain: CustomerDomainI, uuId: string): Promise<CustomerDomainI> {
    return await this.api.post('admin/customerDomain/', {domain, uuId});
  }

  public async customerDomainAdmins(id: string): Promise<UserI[]> {
    return await this.api.get('admin/domain-admin/' + id);
  }

  public async customerDomainAdminCreate(data: { email: string, customer_external_id: string }, uuId: string): Promise<Claim2UserI> {
    return await this.api.post('admin/domain-admin/', data);
  }

  public async customerDomainUpdate(domain: CustomerDomainI, uuId: string): Promise<any> {
    return await this.api.put('admin/customerDomain/', {domain, uuId});
  }

  public async customerDomainDelete(id: string): Promise<any> {
    return await this.api.delete('admin/customerDomain/' + id);
  }

  public async customerUpdate(customer: CustomerI, uuId: string): Promise<any> {
    return await this.api.put('admin/customer/', {customer, uuId});
  }

  public async getActivations(userId: string): Promise<ActivationT[]> {
    return await this.api.get('admin/activations/' + userId);
  }

  public async updateActivation(data: ActivationT): Promise<any> {
    if (data.id) {
      return await this.api.patch('admin/activations/' + data.id, data);
    } else {
      return null;
    }
  }

  public async register(user: Registration): Promise<UserI> {
    return await this.api.post('registration', user);
  }

  // log-routes
  public async getLogs(name?: string): Promise<Array<LogT> | { log: string }> {
    return await this.api.post('admin/logs/', {name: name});
  }
}
